import {
  createContext,
  type CSSProperties,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { useTextOverflow } from "../../../hooks/use-text-overflow";
import { cn } from "../../../lib/utils";
import {
  dropdownChevronClassName,
  dropdownPlaceholderClassName,
  dropdownTriggerClassName,
} from "../dropdown-trigger-styles";
import type { SelectorFieldProps, SelectorTriggerProps } from "./types";
import { type LabelSizing, useFittedLabel } from "./use-fitted-label";

type SelectorFieldContextValue = {
  labelId?: string;
  descriptionId?: string;
  error?: boolean;
  required?: boolean;
};

const SelectorFieldContext = createContext<SelectorFieldContextValue>({});

const SelectorTriggerContext = createContext<{
  maxWidth?: number;
  menuOpen?: boolean;
}>({});

export function SelectorField({
  label,
  hint,
  error,
  required,
  className,
  children,
}: SelectorFieldProps & { children: ReactNode }) {
  const message = typeof error === "string" ? error : hint;
  const generatedId = useId();
  const labelId = label ? `${generatedId}-label` : undefined;
  const descriptionId = message ? `${generatedId}-description` : undefined;

  return (
    <SelectorFieldContext.Provider
      value={{
        labelId,
        descriptionId,
        error: Boolean(error),
        required,
      }}
    >
      <div className={cn("space-y-1", className)}>
        {label ? (
          <div
            id={labelId}
            className="font-inter text-sm font-medium text-neutral-800"
          >
            {label}
            {required ? (
              <span aria-hidden className="ml-0.5 text-red-500">
                *
              </span>
            ) : null}
          </div>
        ) : null}
        {children}
        {message ? (
          <div
            id={descriptionId}
            role={error ? "alert" : undefined}
            className={cn(
              "font-inter text-sm leading-5 text-neutral-500",
              error && "text-red-500"
            )}
          >
            {message}
          </div>
        ) : null}
      </div>
    </SelectorFieldContext.Provider>
  );
}

export const SelectorTrigger = forwardRef<
  HTMLButtonElement,
  SelectorTriggerProps & { children: ReactNode }
>(function SelectorTrigger(
  {
    children,
    size = "md",
    open = false,
    disabled = false,
    error = false,
    clearable = false,
    onClear,
    placeholder,
    maxWidth,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-haspopup": ariaHasPopup,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    "data-state": dataState,
    ...props
  },
  ref
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);
  const field = useContext(SelectorFieldContext);
  const describedBy = [ariaDescribedBy, field.descriptionId]
    .filter(Boolean)
    .join(" ");
  const labelledBy = [ariaLabelledBy, !ariaLabel ? field.labelId : undefined]
    .filter(Boolean)
    .join(" ");
  const clear = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClear?.();
    queueMicrotask(() => triggerRef.current?.focus());
  };
  const contentSized = maxWidth !== undefined;
  // Radix passes the open state as data-state even when a caller omits open.
  const menuOpen = open || dataState === "open";
  const triggerContext = useMemo(
    () => ({ maxWidth, menuOpen }),
    [maxWidth, menuOpen]
  );

  return (
    <span
      className="relative block w-full"
      // A variable, so one max-width class can take the smaller of it and 100%.
      style={
        contentSized
          ? ({ "--selector-trigger-max": `${maxWidth}px` } as CSSProperties)
          : undefined
      }
    >
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy || undefined}
        aria-describedby={describedBy || undefined}
        // The panel is a list of options, whatever Radix's own trigger thinks.
        // `role="combobox"` stays: ~15 consumer tests query the trigger by it.
        aria-haspopup={ariaHasPopup ?? "listbox"}
        aria-invalid={(ariaInvalid ?? (error || field.error)) || undefined}
        aria-required={ariaRequired ?? field.required ?? undefined}
        data-placeholder={placeholder}
        data-state={open ? "open" : dataState}
        disabled={disabled}
        className={cn(
          "justify-between gap-2 rounded-lg font-inter text-sm font-medium",
          contentSized
            ? "w-auto min-w-full max-w-[min(var(--selector-trigger-max),100%)] transition-[color,background-color,border-color,box-shadow]"
            : "w-full",
          size === "md" ? "h-10 px-3.5 py-2.5" : "h-8 px-3 py-1.5",
          dropdownTriggerClassName,
          "active:scale-100 disabled:bg-white disabled:opacity-40 data-[disabled]:bg-white data-[disabled]:opacity-40",
          error &&
            "border-red-500 focus-visible:border-red-500 focus-visible:shadow-[0_0_0_2px_rgba(239,68,68,0.08)] data-[state=open]:border-red-500",
          className
        )}
        {...props}
      >
        <span className="flex min-w-0 flex-1 items-center text-left">
          <SelectorTriggerContext.Provider value={triggerContext}>
            {children}
          </SelectorTriggerContext.Provider>
        </span>
        {clearable && onClear ? (
          <span aria-hidden className="size-5 shrink-0" />
        ) : null}
        <i
          aria-hidden
          className={cn(
            "fa-regular fa-chevron-down size-5 shrink-0 text-center text-[16px] leading-5 transition-transform",
            dropdownChevronClassName,
            open && "rotate-180"
          )}
        />
      </Button>
      {clearable && onClear ? (
        <button
          type="button"
          aria-label="Clear selection"
          disabled={disabled}
          className={cn(
            "absolute top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded p-0 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800 disabled:pointer-events-none",
            disabled && "opacity-40",
            size === "md" ? "right-[42px]" : "right-10"
          )}
          onClick={clear}
        >
          <i
            aria-hidden
            className="fa-regular fa-xmark size-5 shrink-0 text-center text-[16px] leading-5"
          />
        </button>
      ) : null}
    </span>
  );
});

/** Dependency key for a label that is not plain text. Constant on purpose:
 * it must not change identity between renders. */
const NON_TEXT_LABEL = "\u0000non-text-label";

/**
 * The trigger's own label. Plain text is cut after the last whole word that
 * fits, with the full name in a tooltip only when cut. Other children keep a
 * CSS ellipsis, with the same tooltip when clipped.
 *
 * `maxWidth` caps the label itself (a chip); otherwise a trigger given
 * `maxWidth` sets the room, and failing that the label takes what the layout
 * leaves it. `suffix` sits right after the visible text, so a count such as
 * `· 6` stays beside a cut name instead of at the far edge of its box; it
 * brings its own left margin.
 */
export function SelectorTriggerLabel({
  children,
  className,
  maxWidth,
  suffix,
  tooltipOnFocus = true,
}: {
  children: ReactNode;
  className?: string;
  maxWidth?: number;
  suffix?: ReactNode;
  /** Off for several labels inside one combobox (chips): focus would open
   * every tooltip at once. */
  tooltipOnFocus?: boolean;
}) {
  const trigger = useContext(SelectorTriggerContext);
  const text =
    typeof children === "string" || typeof children === "number"
      ? String(children)
      : undefined;
  let sizing: LabelSizing = { mode: "observed" };
  if (maxWidth !== undefined) sizing = { mode: "label", maxWidth };
  else if (trigger.maxWidth !== undefined) {
    sizing = { mode: "trigger", maxWidth: trigger.maxWidth };
  }
  const hasSuffix = suffix !== undefined && suffix !== null;
  const [node, setNode] = useState<HTMLSpanElement | null>(null);
  const [textNode, setTextNode] = useState<HTMLSpanElement | null>(null);
  const [suffixNode, setSuffixNode] = useState<HTMLSpanElement | null>(null);
  const [sizerNode, setSizerNode] = useState<HTMLSpanElement | null>(null);
  const fitted = useFittedLabel(node, text, sizing, {
    textElement: hasSuffix ? textNode : node,
    suffix: hasSuffix ? suffixNode : null,
    sizer: sizerNode,
  });
  // Depend on the resolved text, not the node: a ReactNode's identity changes
  // every render. A non-string label gets a sentinel rather than a stringified
  // node, so it will not re-measure when it changes.
  const { ref, isOverflowing } = useTextOverflow<HTMLSpanElement>([
    text ?? NON_TEXT_LABEL,
    fitted.text,
  ]);
  const [hovered, setHovered] = useState(false);
  const [comboboxFocused, setComboboxFocused] = useState(false);

  const setRefs = useCallback(
    (element: HTMLSpanElement | null) => {
      if (!hasSuffix) ref(element);
      setNode(element);
    },
    [ref, hasSuffix]
  );
  const setTextRefs = useCallback(
    (element: HTMLSpanElement | null) => {
      if (hasSuffix) ref(element);
      setTextNode(element);
    },
    [ref, hasSuffix]
  );

  // This span cannot take focus; the combobox button several levels up can, so
  // watching it is what makes a clipped name readable from the keyboard.
  useEffect(() => {
    const combobox = node?.closest<HTMLElement>('[role="combobox"]');
    if (!combobox) return undefined;
    const onFocus = () => setComboboxFocused(true);
    const onBlur = () => setComboboxFocused(false);
    combobox.addEventListener("focus", onFocus);
    combobox.addEventListener("blur", onBlur);
    return () => {
      combobox.removeEventListener("focus", onFocus);
      combobox.removeEventListener("blur", onBlur);
    };
  }, [node]);

  const clipped = fitted.cut || isOverflowing;
  const visible = fitted.cut ? fitted.text : children;

  const label = (
    <span
      ref={setRefs}
      className={cn(
        "block truncate",
        sizing.mode === "label" && "max-w-(--selector-label-max)",
        className
      )}
      style={
        sizing.mode === "label"
          ? ({
              "--selector-label-max": `${sizing.maxWidth}px`,
            } as CSSProperties)
          : undefined
      }
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {hasSuffix ? (
        <span className="flex min-w-0 items-center">
          <span
            ref={setTextRefs}
            aria-hidden={fitted.cut || undefined}
            className="min-w-0 truncate"
          >
            {visible}
          </span>
          {fitted.cut ? <span className="sr-only">{text}</span> : null}
          <span ref={setSuffixNode} className="shrink-0">
            {suffix}
          </span>
        </span>
      ) : fitted.cut ? (
        <span aria-hidden className="block truncate">
          {fitted.text}
        </span>
      ) : (
        children
      )}
      {fitted.cut ? (
        <>
          {sizing.mode === "observed" ? (
            // Keeps the label's natural width at the full name, so a cut never
            // narrows the box it was cut to fit.
            <span
              ref={setSizerNode}
              aria-hidden
              className="invisible block h-0 overflow-hidden"
            >
              {text}
              {suffix}
            </span>
          ) : null}
          {hasSuffix ? null : <span className="sr-only">{text}</span>}
        </>
      ) : null}
    </span>
  );

  // Mounted whether or not the label fits: swapping between a bare and a
  // wrapped span remounts the node the observers are watching.
  return (
    <Tooltip
      open={
        clipped &&
        !trigger.menuOpen &&
        (hovered || (tooltipOnFocus && comboboxFocused))
      }
    >
      <TooltipTrigger asChild>{label}</TooltipTrigger>
      {clipped ? <TooltipContent>{children}</TooltipContent> : null}
    </Tooltip>
  );
}

export function SelectorPlaceholder({ children }: { children: ReactNode }) {
  return (
    <span className={cn("truncate", dropdownPlaceholderClassName)}>
      {children}
    </span>
  );
}
